import {AppName} from '@hconnect/common/hproduce'
import {TimeZone} from '@hconnect/uikit'
import {
  extendedQuickSelectionItems,
  timeRangeParamToDateRange
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {isEmpty, omit} from 'lodash'
import moment from 'moment-timezone'
import {getI18n} from 'react-i18next'

import {
  ProcessWorkOrderCreateDto,
  ProcessWorkOrderFormValues,
  ShiftTaskDto,
  TaskFilterDto,
  TasksFilterQueryParams,
  TasksFiltersState,
  TaskSource,
  TasksSortBy,
  TasksStatusSummaryFilterQueryParams,
  TasksTableKey,
  UseTasksParams,
  UseTasksStatusesCountsParams
} from '../types'

const SERVER_APP_NAME_MAP: Record<TaskSource, AppName> = {
  [TaskSource.COCKPIT]: 'Cockpit',
  [TaskSource.EASY_POM]: 'easyPOM',
  [TaskSource.PROCESS_WORK_ORDER]: 'HProduce',
  [TaskSource.MACHINE_SENTRY_ACTIONS]: 'HProduce',
  [TaskSource.OIL_ANALYSIS_ACTIONS]: 'HProduce',
  [TaskSource.DALOG_ACTIONS]: 'HProduce'
}

export const mapToAppName = (source: TaskSource): AppName =>
  SERVER_APP_NAME_MAP[source] ?? 'HProduce'

export const mapTasksFiltersParams = (
  timezone: TimeZone,
  filters?: TasksFiltersState,
  areaPlantIds?: string[]
): TaskFilterDto | undefined => {
  if (!filters || isEmpty(filters)) {
    return undefined
  }

  const {
    plant,
    oilAnalysisCriticality,
    assignee,
    createdBy,
    mainEquipment,
    equipment,
    pomActionSources,
    pomSections,
    pomTopics,
    pomQuestions,
    pomQuestionCategories,
    pomCostLeadership,
    dueDateRange,
    createdDateRange,
    ...restFilters
  } = omit(filters, 'area')

  const {from: dueDateFrom, to: dueDateTo} = mapTimeRangeParam(timezone, dueDateRange)
  const {from: createdDateFrom, to: createdDateTo} = mapTimeRangeParam(
    timezone,
    createdDateRange,
    true
  )

  const resolvedPlantIds = plant ? [plant] : areaPlantIds

  return {
    ...restFilters,
    oilAnalysisCriticalities: oilAnalysisCriticality,
    plantIds: resolvedPlantIds,
    assigneeIds: assignee ? [assignee] : undefined,
    createdByIds: createdBy ? [createdBy] : undefined,
    mainEquipmentId: mainEquipment,
    equipmentId: equipment,
    actionSources: pomActionSources,
    sectionIds: pomSections,
    topicIds: pomTopics,
    questionIds: pomQuestions,
    questionDifficulties: pomQuestionCategories,
    costLeadership: pomCostLeadership ? pomCostLeadership === 'true' : undefined,
    dueDateFrom,
    dueDateTo,
    createdDateFrom,
    createdDateTo
  }
}

export const mapTasksQueryParams = (
  params: UseTasksParams,
  offset: number
): TasksFilterQueryParams => {
  return {
    offset,
    limit: params.limit,
    sources: params.sources,
    includeOnlyAssignedToUser: params.assignedToMe,
    includeOnlyCreatedByUser: params.createdByMe,
    sortBy: params.sorting.sortBy,
    sortOrder: params.sorting.sortOrder
  }
}

export const mapTasksStatusesSummaryQueryParams = (
  params: UseTasksStatusesCountsParams
): TasksStatusSummaryFilterQueryParams => {
  return {
    sources: params.sources,
    includeOnlyAssignedToUser: params.assignedToMe,
    includeOnlyCreatedByUser: params.createdByMe
  }
}

const mapTimeRangeParam = (
  timezone: TimeZone,
  timeRange?: string,
  snapEndToEndOfDay = false
): {from?: string; to?: string} => {
  const selectionItems = extendedQuickSelectionItems(getI18n().t, timezone)
  const [from, to] = timeRangeParamToDateRange({timeRange, selectionItems})

  // the "to" date is inclusive, so snap it to the end of the selected day in the plant timezone
  const resolvedTo = to && snapEndToEndOfDay ? moment.tz(to, timezone).endOf('day').toDate() : to

  return {from: from?.toISOString(), to: resolvedTo?.toISOString()}
}

const tasksSortByMap: Partial<Record<TasksTableKey, TasksSortBy>> = {
  [TasksTableKey.TITLE]: TasksSortBy.TITLE,
  [TasksTableKey.DUE_DATE]: TasksSortBy.DUE_DATE,
  [TasksTableKey.CREATED]: TasksSortBy.CREATED_AT,
  [TasksTableKey.STATUS]: TasksSortBy.STATUS,
  [TasksTableKey.ASSIGNEE]: TasksSortBy.ASSIGNEE,
  [TasksTableKey.MAINTENANCE_NOTIFICATION]: TasksSortBy.MAINTENANCE_NOTIFICATION_ID,
  [TasksTableKey.PLANT]: TasksSortBy.PLANT_NAME
}

const tasksTableKeyMap: Partial<Record<TasksSortBy, TasksTableKey>> = {
  [TasksSortBy.TITLE]: TasksTableKey.TITLE,
  [TasksSortBy.DUE_DATE]: TasksTableKey.DUE_DATE,
  [TasksSortBy.CREATED_AT]: TasksTableKey.CREATED,
  [TasksSortBy.STATUS]: TasksTableKey.STATUS,
  [TasksSortBy.ASSIGNEE]: TasksTableKey.ASSIGNEE,
  [TasksSortBy.MAINTENANCE_NOTIFICATION_ID]: TasksTableKey.MAINTENANCE_NOTIFICATION,
  [TasksSortBy.PLANT_NAME]: TasksTableKey.PLANT
}

export const mapTasksTableKeyToSortBy = (tableKey?: TasksTableKey): TasksSortBy | undefined => {
  return tasksSortByMap[tableKey as TasksTableKey]
}

export const mapTasksSortByToTableKey = (sortBy?: TasksSortBy): TasksTableKey | undefined => {
  return tasksTableKeyMap[sortBy as TasksSortBy]
}

export const mapProcessWorkOrderFormValuesToCreateDto = (
  formValues: ProcessWorkOrderFormValues
): ProcessWorkOrderCreateDto => {
  const {checklists, ...rest} = formValues

  return {
    ...rest,
    equipment: formValues.equipment?.equipment,
    checklistTemplateIds: checklists?.map((checklist) => checklist.id) || [],
    attachments:
      formValues.attachments?.map((attachment: File) => ({
        name: attachment.name,
        contentType: attachment.type,
        preview: URL.createObjectURL(attachment),
        isImage: attachment.type.includes('image'),
        originalFile: attachment
      })) || []
  }
}

export const mapEventInfoItem = (data: ShiftTaskDto) => ({
  ...data,
  dueDate: data.dueDate ? moment(data.dueDate) : undefined,
  created: data.created ? moment(data.created) : undefined,
  lastModified: data.lastModified ? moment(data.lastModified) : undefined,
  lastModifiedStatus: data.lastModifiedStatus ? moment(data.lastModifiedStatus) : undefined,
  doNotStartBefore: data.doNotStartBefore ? moment(data.doNotStartBefore) : undefined
})
